import {FC, useEffect, useState} from 'react'
import {api, InfoPage, ApiError} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {Markdown} from '../../components/Markdown'
import {PageHead} from '../../components/PageHead'
import {ModalPortal} from '../../components/ModalPortal'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const InfoPagesPage: FC = () => {
  const toast = useToast()
  const [pages, setPages] = useState<InfoPage[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<InfoPage | null>(null)

  useEffect(() => {
    api.adminInfoPages().then(setPages).catch((e) => toast.error(e instanceof ApiError ? e.message : 'Failed to load')).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <PageHead icon='📄' eyebrow='Content' title='Info Pages' sub='Edit the house guidelines, policies and transport guide shown to guests and renters.' />

      {loading ? (
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading…</div>
      ) : (
        <div className='row g-3 ccp-stagger'>
          {pages.map((p) => (
            <div className='col-12 col-md-6 col-lg-4' key={p.id}>
              <div className='ccp-lift' style={{background: '#fff', borderRadius: 14, padding: '20px 22px', boxShadow: '0 6px 18px rgba(27,27,41,0.06)', border: '1px solid #eef0f4', height: '100%', display: 'flex', flexDirection: 'column'}}>
                <div style={{fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em'}}>/info/{p.slug}</div>
                <div style={{fontSize: 18, fontWeight: 800, color: INK, margin: '4px 0 10px'}}>{p.title}</div>
                <div style={{fontSize: 13, color: MUTED, flex: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical'}}>
                  {p.body.replace(/[#>*`_-]/g, '').slice(0, 160)}…
                </div>
                <button onClick={() => setEditing(p)} style={{...btnPrimary, marginTop: 14, alignSelf: 'flex-start'}}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ModalPortal><EditModal
          page={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setPages((l) => l.map((x) => (x.id === saved.id ? saved : x)))
            setEditing(null)
            toast.success(`${saved.title} updated.`)
          }}
        /></ModalPortal>
      )}
    </div>
  )
}

const EditModal: FC<{page: InfoPage; onClose: () => void; onSaved: (p: InfoPage) => void}> = ({page, onClose, onSaved}) => {
  const [title, setTitle] = useState(page.title)
  const [body, setBody] = useState(page.body)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    setErr(null)
    if (!title.trim()) return setErr('Title is required.')
    if (!body.trim()) return setErr('Body is required.')
    setBusy(true)
    try {
      onSaved(await api.updateInfoPage(page.id, {title: title.trim(), body}))
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save')
      setBusy(false)
    }
  }

  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, width: '100%', maxWidth: 1000, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'}}>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f1f3f7'}}>
          <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: 0}}>Edit /info/{page.slug}</h2>
          <button onClick={onClose} aria-label='Close' style={{background: '#f1f3f7', border: 'none', width: 30, height: 30, borderRadius: '50%', fontSize: 18, lineHeight: 1, cursor: 'pointer', color: MUTED}}>×</button>
        </div>

        <div style={{padding: '20px 24px'}}>
          {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{err}</div>}

          <label style={lbl}>Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} style={{...inp, marginBottom: 16}} />

          <div className='row g-3'>
            <div className='col-12 col-lg-6'>
              <label style={lbl}>Body (Markdown)</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} style={{...inp, minHeight: 380, resize: 'vertical', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, lineHeight: 1.6}} />
            </div>
            <div className='col-12 col-lg-6'>
              <label style={lbl}>Preview</label>
              <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', minHeight: 380, maxHeight: 500, overflowY: 'auto', background: '#fff'}}>
                <Markdown>{body || '_Nothing to preview yet._'}</Markdown>
              </div>
            </div>
          </div>
        </div>

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #f1f3f7'}}>
          <button onClick={onClose} style={btnGhost} disabled={busy}>Cancel</button>
          <button onClick={save} style={{...btnPrimary, minWidth: 130}} disabled={busy}>{busy ? <Spinner size={14} /> : 'Save changes'}</button>
        </div>
      </div>
    </div>
  )
}

const lbl: React.CSSProperties = {display: 'block', fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6}
const inp: React.CSSProperties = {width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: INK, background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}
const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '9px 16px', cursor: 'pointer', border: '1px solid transparent'}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnGhost: React.CSSProperties = {...btnBase, background: '#fff', border: '1px solid #e5e7eb', color: INK}

export {InfoPagesPage}
